import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ListTree, Plus, Pencil, Trash2, Settings, X, Link2 } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import {
  getSteps, createStep, updateStep, deleteStep,
  getStraw, createStraw, updateStraw,
  addStrawHeader, removeStrawHeader,
  addStrawQuery, removeStrawQuery,
  addStrawBody, removeStrawBody,
  addStrawResponse, removeStrawResponse,
  addStrawResponseObject, removeStrawResponseObject,
  getDrinkers,
  getInterviewer, createInterviewer, updateInterviewer,
  getInterviewerParts, addInterviewerPart, updateInterviewerPart, removeInterviewerPart,
  addInterviewerPartItem, removeInterviewerPartItem, updateInterviewerPartItem, getPreparations,
  getStepFunnelConnector, setStepFunnelConnector,
  getCups, getCupTraits, getCupChildRelations,
  getStepGenerator, createStepGenerator, updateStepGenerator,
  addGeneratorOutput, updateGeneratorOutput, deleteGeneratorOutput,
  getStepDocumentGenerator, createStepDocumentGenerator, updateStepDocumentGenerator, getCupGlobalRelations,
} from '../api/brainer'
import { listFunnelConnectors } from '../api/funnel'
import { getDatasets } from '../api/storage'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import type { BStep, BStraw, BDrinker, BInterviewer, BInterviewerPart, BInterviewerPartItem, BInterviewerPartItemType, BInterviewerPartItemInputMode, BStepFunnelConnector, BCup, BCupTrait, BCupRelation, FunnelConnector, BStepGenerator, BStepGeneratorOutput, BStepDocumentGenerator, Dataset, BPreparation, BCupGlobalRelation } from '../types'
import { useNavigate } from 'react-router-dom'
import { GLOBAL_CLASS_TITLES } from '../api/global-options'

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH']

const typeColor: Record<string, string> = {
  straw: 'bg-blue-50 text-blue-700',
  drinker: 'bg-amber-50 text-amber-700',
  interviewer: 'bg-green-50 text-green-700',
  connector: 'bg-violet-50 text-violet-700',
  generator: 'bg-orange-50 text-orange-700',
  document_generator: 'bg-rose-50 text-rose-700',
}

export function BrainerSteps() {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const { data: steps = [], isLoading } = useQuery({ queryKey: ['b-steps'], queryFn: () => getSteps().then((r) => r.data) })
  const { data: cups = [] } = useQuery({ queryKey: ['b-cups'], queryFn: () => getCups().then((r) => r.data) })

  // Create step
  const [showCreate, setShowCreate] = useState(false)
  const [cForm, setCForm] = useState({ type: 'straw' as BStep['type'], title: '', description: '' })
  const create = useMutation({
    mutationFn: () => createStep(cForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-steps'] }); setShowCreate(false); setCForm({ type: 'straw', title: '', description: '' }) },
  })

  // Edit step
  const [editStep, setEditStep] = useState<BStep | null>(null)
  const [eForm, setEForm] = useState({ title: '', description: '' })
  const openEdit = (s: BStep) => { setEditStep(s); setEForm({ title: s.title, description: s.description ?? '' }) }
  const saveStep = useMutation({
    mutationFn: () => updateStep(editStep!.id, { title: eForm.title, description: eForm.description || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-steps'] }); setEditStep(null) },
  })

  // Delete step
  const [deleteTarget, setDeleteTarget] = useState<BStep | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deleteStep(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-steps'] }); setDeleteTarget(null) },
  })

  // Config panel
  const [configStep, setConfigStep] = useState<BStep | null>(null)
  const [search, setSearch] = useState('')
  const filtered = steps.filter((s: BStep) =>
    [s.title, s.description ?? '', s.type].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Steps</h1>
          <p className="text-sm text-ink-dim mt-0.5">Reusable action units  API calls, scripts, or data collection forms</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search steps" />
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New step</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : steps.length === 0 ? (
          <EmptyState icon={ListTree} title="No steps" description="Create reusable action steps to compose into preparations" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s: BStep) => (
                <tr key={s.id}>
                  <td>
                    <p className="font-medium text-ink">{s.title}</p>
                    {s.description && <p className="text-xs text-ink-dim mt-0.5">{s.description}</p>}
                  </td>
                  <td>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${typeColor[s.type]}`}>{s.type}</span>
                  </td>
                  <td className="text-ink-dim">{fmt(s.createdAt)}</td>
                  <td>
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => setConfigStep(s)} className="flex items-center gap-1 text-xs text-ink-dim hover:text-ink font-medium"><Settings size={13} /> Config</button>
                      <button onClick={() => openEdit(s)} className="text-ink-dim hover:text-ink transition-colors"><Pencil size={13} /></button>
                      <button onClick={() => setDeleteTarget(s)} className="text-ink-dim hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal title="New step" onClose={() => setShowCreate(false)} size="sm">
          <div className="space-y-4">
            <Field label="Type *">
              <Select value={cForm.type} onValueChange={(v) => setCForm((f) => ({ ...f, type: v as BStep['type'] }))}>
                <SelectItem value="straw">straw — HTTP API call</SelectItem>
                <SelectItem value="drinker">drinker — JS script</SelectItem>
                <SelectItem value="interviewer">interviewer — data collection form</SelectItem>
                <SelectItem value="connector">connector — funnel connector run</SelectItem>
                <SelectItem value="generator">generator — auto-generate bean values</SelectItem>
                <SelectItem value="document_generator">document generator — fill a template, attach the result</SelectItem>
              </Select>
            </Field>
            <Field label="Title *">
              <input value={cForm.title} onChange={(e) => setCForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Fetch user profile"
                className="field" />
            </Field>
            <Field label="Description">
              <input value={cForm.description} onChange={(e) => setCForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional"
                className="field" />
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!cForm.title || create.isPending} loading={create.isPending}>
              Create
            </Button>
          </div>
        </Modal>
      )}

      {/* Edit modal */}
      {editStep && (
        <Modal title="Edit step" onClose={() => setEditStep(null)} size="sm">
          <div className="space-y-4">
            <Field label="Title *">
              <input value={eForm.title} onChange={(e) => setEForm((f) => ({ ...f, title: e.target.value }))}
                className="field" />
            </Field>
            <Field label="Description">
              <input value={eForm.description} onChange={(e) => setEForm((f) => ({ ...f, description: e.target.value }))}
                className="field" />
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setEditStep(null)}>Cancel</Button>
            <Button onClick={() => saveStep.mutate()} disabled={!eForm.title || saveStep.isPending} loading={saveStep.isPending}>
              Save
            </Button>
          </div>
        </Modal>
      )}

      {/* Config modal */}
      {configStep && (
        <StepConfigModal step={configStep} onClose={() => setConfigStep(null)} cups={cups} />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete step"
          message={`Delete step "${deleteTarget.title}"?`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}
    </div>
  )
}

function StepConfigModal({ step, onClose, cups }: { step: BStep; onClose: () => void; cups: BCup[] }) {
  if (step.type === 'straw') return <StrawConfig step={step} onClose={onClose} />
  if (step.type === 'drinker') return <DrinkerConfig step={step} onClose={onClose} />
  if (step.type === 'connector') return <ConnectorStepConfig step={step} onClose={onClose} />
  if (step.type === 'generator') return <GeneratorConfig step={step} onClose={onClose} />
  if (step.type === 'document_generator') return <DocumentGeneratorConfig step={step} onClose={onClose} />
  return <InterviewerConfig step={step} onClose={onClose} cups={cups} />
}

function StrawConfig({ step, onClose }: { step: BStep; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: straw, isLoading } = useQuery<BStraw | null>({
    queryKey: ['b-straw', step.id],
    queryFn: () => getStraw(step.id).then((r) => r.data).catch(() => null),
  })

  const [url, setUrl] = useState('')
  const [method, setMethod] = useState('GET')
  useEffect(() => { if (straw) setMethod(straw.method) }, [straw])
  const createSt = useMutation({
    mutationFn: () => createStraw(step.id, { url, method }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-straw', step.id] }),
  })
  const updateSt = useMutation({
    mutationFn: () => updateStraw(straw!.id, { url: url || straw!.url, method: (method || straw!.method) as any }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-straw', step.id] }),
  })

  const [hForm, setHForm] = useState({ title: '', value: '', description: '' })
  const addH = useMutation({
    mutationFn: () => addStrawHeader(straw!.id, hForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-straw', step.id] }); setHForm({ title: '', value: '', description: '' }) },
  })
  const delH = useMutation({ mutationFn: (id: string) => removeStrawHeader(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['b-straw', step.id] }) })

  const [qForm, setQForm] = useState({ title: '', value: '' })
  const addQ = useMutation({
    mutationFn: () => addStrawQuery(straw!.id, qForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-straw', step.id] }); setQForm({ title: '', value: '' }) },
  })
  const delQ = useMutation({ mutationFn: (id: string) => removeStrawQuery(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['b-straw', step.id] }) })

  const [bForm, setBForm] = useState({ key: '', value: '' })
  const addB = useMutation({
    mutationFn: () => addStrawBody(straw!.id, bForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-straw', step.id] }); setBForm({ key: '', value: '' }) },
  })
  const delB = useMutation({ mutationFn: (id: string) => removeStrawBody(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['b-straw', step.id] }) })

  const [rForm, setRForm] = useState({ code: '200' })
  const addR = useMutation({
    mutationFn: () => addStrawResponse(straw!.id, { code: +rForm.code }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-straw', step.id] }); setRForm({ code: '200' }) },
  })
  const delR = useMutation({ mutationFn: (id: string) => removeStrawResponse(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['b-straw', step.id] }) })

  const delRO = useMutation({ mutationFn: (id: string) => removeStrawResponseObject(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['b-straw', step.id] }) })

  // Per-response object add forms
  const [roForms, setRoForms] = useState<Record<string, { title: string; value: string }>>({})
  const addROm = useMutation({ mutationFn: ({ responseId, d }: { responseId: string; d: { title: string; value: string } }) => addStrawResponseObject(responseId, d), onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-straw', step.id] }) } })

  if (isLoading) return <Modal title={`Config  ${step.title}`} onClose={onClose}><div className="py-8 text-center text-sm text-ink-dim">Loading</div></Modal>

  return (
    <Modal title={`Straw config  ${step.title}`} onClose={onClose} size="lg">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
        {/* URL & Method */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-dim mb-3">Endpoint</h3>
          {!straw ? (
            <div className="flex gap-2">
              <Select value={method} onValueChange={setMethod} className="w-28">
                {HTTP_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </Select>
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.example.com/{{param}}"
                className="flex-1 field" />
              <Button onClick={() => createSt.mutate()} disabled={!url || createSt.isPending} loading={createSt.isPending}>
                Save
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Select value={method || straw.method} onValueChange={setMethod} className="w-28">
                {HTTP_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </Select>
              <input defaultValue={straw.url} onChange={(e) => setUrl(e.target.value)} placeholder={straw.url}
                className="flex-1 field" />
              <Button onClick={() => updateSt.mutate()} disabled={updateSt.isPending} loading={updateSt.isPending}>
                Update
              </Button>
            </div>
          )}
          <p className="text-xs text-ink-dim mt-1.5">Use <code className="bg-paper-sunken px-1 rounded">{'{{paramName}}'}</code> for dynamic values from the journey payload.</p>
        </section>

        {straw && (
          <>
            {/* Headers */}
            <KVSection title="Headers" items={straw.headers ?? []}
              form={hForm} setForm={setHForm}
              fields={[{ k: 'title', ph: 'Header name' }, { k: 'value', ph: 'Value / {{token}}' }]}
              onAdd={() => addH.mutate()} addPending={addH.isPending}
              onRemove={(id) => delH.mutate(id)}
              render={(h) => <><span className="font-medium">{h.title}</span><span className="text-neutral-400 ml-2">{h.value}</span></>}
            />

            {/* Query params */}
            <KVSection title="Query params" items={straw.queries ?? []}
              form={qForm} setForm={setQForm}
              fields={[{ k: 'title', ph: 'Param name' }, { k: 'value', ph: 'Value / {{param}}' }]}
              onAdd={() => addQ.mutate()} addPending={addQ.isPending}
              onRemove={(id) => delQ.mutate(id)}
              render={(q) => <><span className="font-medium">{q.title}</span><span className="text-neutral-400 ml-2">= {q.value}</span></>}
            />

            {/* Body */}
            <KVSection title="Body fields" items={straw.bodies ?? []}
              form={bForm} setForm={setBForm}
              fields={[{ k: 'key', ph: 'Field key' }, { k: 'value', ph: 'Value / {{param}}' }]}
              onAdd={() => addB.mutate()} addPending={addB.isPending}
              onRemove={(id) => delB.mutate(id)}
              render={(b) => <><span className="font-medium">{b.key}</span><span className="text-neutral-400 ml-2">: {b.value}</span></>}
            />

            {/* Responses */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Responses</h3>
              <div className="flex gap-2 mb-3">
                <input type="number" value={rForm.code} onChange={(e) => setRForm({ code: e.target.value })} placeholder="200"
                  className="w-24 field" />
                <Button onClick={() => addR.mutate()} disabled={addR.isPending} icon={Plus} />
              </div>
              {(straw.responses ?? []).map((resp) => (
                <div key={resp.id} className="mb-3 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">HTTP {resp.code}</span>
                    <button onClick={() => delR.mutate(resp.id)} className="text-neutral-300 hover:text-red-500 transition-colors"><X size={13} /></button>
                  </div>
                  <div className="space-y-1 mb-2">
                    {(resp.objects ?? []).map((obj) => (
                      <div key={obj.id} className="flex items-center gap-2 text-xs text-neutral-600">
                        <span className="font-medium">{obj.title}</span>
                        {obj.value && <span className="text-neutral-400">← {obj.value}</span>}
                        <button onClick={() => delRO.mutate(obj.id)} className="ml-auto text-neutral-300 hover:text-red-500"><X size={11} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input placeholder="Key name"
                      value={roForms[resp.id]?.title ?? ''}
                      onChange={(e) => setRoForms((f) => ({ ...f, [resp.id]: { ...f[resp.id], title: e.target.value } }))}
                      className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400" />
                    <input placeholder="JSON path (e.g. data.id)"
                      value={roForms[resp.id]?.value ?? ''}
                      onChange={(e) => setRoForms((f) => ({ ...f, [resp.id]: { ...f[resp.id], value: e.target.value } }))}
                      className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400" />
                    <button onClick={() => addROm.mutate({ responseId: resp.id, d: roForms[resp.id] ?? { title: '', value: '' } })}
                      disabled={!roForms[resp.id]?.title || addROm.isPending}
                      className="px-2 py-1.5 text-xs rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"><Plus size={11} /></button>
                  </div>
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </Modal>
  )
}

function DrinkerConfig({ step, onClose }: { step: BStep; onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { data: drinkers = [], isLoading } = useQuery<BDrinker[]>({
    queryKey: ['b-drinkers'],
    queryFn: () => getDrinkers().then(r => r.data),
  })

  const [selectedId, setSelectedId] = useState(step.drinkerId ?? '')

  const assign = useMutation({
    mutationFn: () => updateStep(step.id, { drinkerId: selectedId || null } as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-steps'] }); onClose() },
  })

  if (isLoading) return (
    <Modal title={`Drinker -- ${step.title}`} onClose={onClose}>
      <div className="py-8 text-center text-sm text-neutral-400">Loading</div>
    </Modal>
  )

  const current = (drinkers as BDrinker[]).find(d => d.id === step.drinkerId)

  return (
    <Modal title={`Drinker -- ${step.title}`} onClose={onClose} size="sm">
      <div className="space-y-4">
        {current && (
          <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2.5 text-xs">
            <div className="font-medium text-amber-800 mb-0.5">{current.title}</div>
            <div className="text-amber-600 font-mono">? {current.resultKey}</div>
          </div>
        )}

        <Field label="Assign drinker">
          <select
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            className="field"
          >
            <option value="">-- none --</option>
            {(drinkers as BDrinker[]).map(d => (
              <option key={d.id} value={d.id}>{d.title} (? {d.resultKey})</option>
            ))}
          </select>
        </Field>

        <p className="text-xs text-neutral-400">
          Manage scripts on the{' '}
          <button
            onClick={() => { onClose(); navigate('/brainer/drinkers') }}
            className="text-blue-600 hover:underline"
          >
            Drinkers page
          </button>.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => assign.mutate()}
            disabled={assign.isPending}
          >
            {assign.isPending ? 'Saving--' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function InterviewerConfig({ step, onClose, cups }: { step: BStep; onClose: () => void; cups: BCup[] }) {
  const qc = useQueryClient()
  const { data: interviewer, isLoading } = useQuery<BInterviewer | null>({
    queryKey: ['b-interviewer', step.id],
    queryFn: () => getInterviewer(step.id).then((r) => r.data).catch(() => null),
  })
  const { data: drinkers = [] } = useQuery<BDrinker[]>({
    queryKey: ['b-drinkers'],
    queryFn: () => getDrinkers().then(r => r.data),
  })
  const { data: datasets = [] } = useQuery<Dataset[]>({
    queryKey: ['storage-datasets'],
    queryFn: () => getDatasets().then(r => r.data),
  })
  const [cupId, setCupId] = useState('')
  const [payloadKey, setPayloadKey] = useState('')
  const [postProcessDrinkerId, setPostProcessDrinkerId] = useState('')
  const [storageDatasetId, setStorageDatasetId] = useState('')
  const [layout, setLayout] = useState<'wizard' | 'page'>('wizard')
  const [step_, setStep_] = useState(1)
  const stepLabels = ['Interviewer settings', 'Wizard parts']

  useEffect(() => {
    if (interviewer) {
      setCupId(interviewer.cupId ?? '')
      setPayloadKey(interviewer.payloadKey ?? '')
      setPostProcessDrinkerId(interviewer.postProcessDrinkerId ?? '')
      setStorageDatasetId(interviewer.storageDatasetId ?? '')
      setLayout(interviewer.layout ?? 'wizard')
    }
  }, [interviewer])

  const create = useMutation({
    mutationFn: () => createInterviewer(step.id, {
      cupId,
      payloadKey: payloadKey || undefined,
      postProcessDrinkerId: postProcessDrinkerId || undefined,
      storageDatasetId: storageDatasetId || undefined,
      layout,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-interviewer', step.id] }),
  })
  const update = useMutation({
    mutationFn: () => updateInterviewer(interviewer!.id, {
      cupId: cupId || undefined,
      payloadKey: payloadKey || undefined,
      postProcessDrinkerId: postProcessDrinkerId || null,
      storageDatasetId: storageDatasetId || null,
      layout,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-interviewer', step.id] }),
  })
  const saveSettings = () => (interviewer ? update : create).mutate()
  const savingSettings = create.isPending || update.isPending
  const canNext = step_ === 1 ? !!cupId : true

  if (isLoading) return <Modal title={`Config — ${step.title}`} onClose={onClose}><div className="py-8 text-center text-sm text-neutral-400">Loading</div></Modal>

  return (
    <Modal title={`Interviewer config — ${step.title}`} onClose={onClose} size="lg">
      <div className="space-y-5 max-h-[80vh] overflow-y-auto pr-1">
        <p className="text-xs text-neutral-500">An interviewer step pauses the journey and waits for a user to fill a record form.</p>

        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-2">
                <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium shrink-0 ${
                  step_ === i + 1 ? 'bg-blue-600 text-white' : step_ > i + 1 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'
                }`}>
                  {i + 1}
                </span>
                <span className={`text-xs whitespace-nowrap ${step_ === i + 1 ? 'text-neutral-800 font-medium' : 'text-neutral-400'}`}>{label}</span>
              </div>
              {i < stepLabels.length - 1 && <div className={`flex-1 h-px ${step_ > i + 1 ? 'bg-blue-200' : 'bg-slate-100'}`} />}
            </div>
          ))}
        </div>

        {step_ === 1 && (
          <div className="space-y-3">
            <Field label="Cup (data schema) *">
              <select value={cupId} onChange={(e) => setCupId(e.target.value)} className="field">
                <option value="">Select cup</option>
                {cups.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </Field>
            {interviewer && <p className="text-xs text-neutral-400">Current cup: <span className="font-medium">{interviewer.cup?.title ?? interviewer.cupId}</span></p>}

            <Field label="Payload key" description="Key in the accumulated journey payload holding an existing coffee ID for pre-fill (e.g. coffeeId)">
              <input
                value={payloadKey}
                onChange={(e) => setPayloadKey(e.target.value)}
                placeholder="coffeeId"
                className="field font-mono"
              />
            </Field>

            <Field label="Post-process Drinker" description="Script that runs after form submission, inside the same transaction, before mandatory-field validation. Use it to compute derived beans (e.g. pricing).">
              <select
                value={postProcessDrinkerId}
                onChange={(e) => setPostProcessDrinkerId(e.target.value)}
                className="field"
              >
                <option value="">None</option>
                {drinkers.map(d => (
                  <option key={d.id} value={d.id}>{d.title}</option>
                ))}
              </select>
            </Field>
            {interviewer?.postProcessDrinkerId && (
              <p className="text-xs text-neutral-400">
                Current: <span className="font-medium">
                  {drinkers.find(d => d.id === interviewer.postProcessDrinkerId)?.title ?? interviewer.postProcessDrinkerId}
                </span>
              </p>
            )}

            <Field label="Storage dataset" description="Dataset used to store documents uploaded via _document/_documentPath traits. Required when the cup has document-type traits.">
              <select
                value={storageDatasetId}
                onChange={(e) => setStorageDatasetId(e.target.value)}
                className="field"
              >
                <option value="">— none —</option>
                {(datasets as Dataset[]).map(d => (
                  <option key={d.id} value={d.id}>{d.title}</option>
                ))}
              </select>
            </Field>
            <Field label="Form layout" description="Step-by-step: one part per screen with Next/Back. Single page: every part as a card section with one Save button.">
              <select value={layout} onChange={(e) => setLayout(e.target.value as 'wizard' | 'page')} className="field">
                <option value="wizard">Step-by-step wizard</option>
                <option value="page">Single page (card sections)</option>
              </select>
            </Field>

            {interviewer?.storageDatasetId && (
              <p className="text-xs text-neutral-400">
                Current: <span className="font-medium">
                  {(datasets as Dataset[]).find(d => d.id === interviewer.storageDatasetId)?.title ?? interviewer.storageDatasetId}
                </span>
              </p>
            )}
          </div>
        )}

        {step_ === 2 && interviewer && <PartsEditor interviewer={interviewer} />}
        {step_ === 2 && !interviewer && (
          <p className="text-xs text-neutral-400 py-6 text-center">Save the interviewer settings first to configure wizard parts.</p>
        )}

        <div className="flex justify-end gap-2 pb-2 border-b border-slate-100">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {step_ > 1 && <Button variant="secondary" onClick={() => setStep_(s => s - 1)}>Back</Button>}
          {step_ < stepLabels.length ? (
            <Button
              onClick={() => {
                if (!interviewer || cupId !== interviewer.cupId || payloadKey !== (interviewer.payloadKey ?? '') || postProcessDrinkerId !== (interviewer.postProcessDrinkerId ?? '') || storageDatasetId !== (interviewer.storageDatasetId ?? '') || layout !== (interviewer.layout ?? 'wizard')) {
                  saveSettings()
                }
                setStep_(s => s + 1)
              }}
              disabled={!canNext || savingSettings}
            >
              {savingSettings ? 'Saving' : 'Next'}
            </Button>
          ) : (
            <Button onClick={saveSettings} disabled={!canNext || savingSettings}>
              {savingSettings ? 'Saving' : interviewer ? 'Update' : 'Save'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

// A relation item's optional "alternate action" link, shown under the field in the form (e.g.
// "+ Add one-time article" under an Article picker): picking a Preparation here makes the link run
// it in a nested modal with the same journey inputs.
function AltLinkButton({ item, onSaved }: { item: BInterviewerPartItem; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [prepId, setPrepId] = useState(item.altPreparationId ?? '')
  const [label, setLabel] = useState(item.altLabel ?? '')
  const { data: preps = [] } = useQuery<BPreparation[]>({
    queryKey: ['b-preparations'],
    queryFn: () => getPreparations().then(r => r.data),
    enabled: open,
  })
  const save = useMutation({
    mutationFn: () => updateInterviewerPartItem(item.id, { altPreparationId: prepId || null, altLabel: prepId ? (label || null) : null }),
    onSuccess: () => { setOpen(false); onSaved() },
  })
  return (
    <>
      <button onClick={() => setOpen(true)} title="Alternate action link" className="ml-auto text-neutral-300 hover:text-brand-500"><Link2 size={11} /></button>
      {open && (
        <Modal title="Alternate action link" onClose={() => setOpen(false)} size="sm">
          <div className="space-y-4">
            <Field label="Preparation" description="Runs instead of picking a value, in a nested form with the same inputs. When it finishes, this form closes.">
              <select value={prepId} onChange={e => setPrepId(e.target.value)} className="field">
                <option value="">— none —</option>
                {preps.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Link text">
              <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Add one-time article" className="field" />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving' : 'Save'}</Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

function PartsEditor({ interviewer }: { interviewer: BInterviewer }) {
  const qc = useQueryClient()
  const cupId = interviewer.cupId
  const invId = interviewer.id

  const { data: parts = [] } = useQuery<BInterviewerPart[]>({
    queryKey: ['b-interviewer-parts', invId],
    queryFn: () => getInterviewerParts(invId).then(r => r.data),
  })
  const { data: cupTraits = [] } = useQuery<BCupTrait[]>({
    queryKey: ['b-cup-traits', cupId],
    queryFn: () => getCupTraits(cupId).then(r => r.data),
    enabled: !!cupId,
  })
  const { data: cupRelations = [] } = useQuery<BCupRelation[]>({
    queryKey: ['b-cup-child-relations', cupId],
    queryFn: () => getCupChildRelations(cupId).then(r => r.data),
    enabled: !!cupId,
  })
  const { data: allCups = [] } = useQuery<BCup[]>({
    queryKey: ['b-cups'],
    queryFn: () => getCups().then(r => r.data),
  })

  const [newPartTitle, setNewPartTitle] = useState('')
  const addPart = useMutation({
    mutationFn: () => addInterviewerPart(invId, { title: newPartTitle, position: parts.length }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-interviewer-parts', invId] }); setNewPartTitle('') },
  })
  const removePart = useMutation({
    mutationFn: (partId: string) => removeInterviewerPart(partId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-interviewer-parts', invId] }),
  })
  const renamePart = useMutation({
    mutationFn: ({ partId, title }: { partId: string; title: string }) => updateInterviewerPart(partId, { title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-interviewer-parts', invId] }),
  })
  const addItem = useMutation({
    mutationFn: ({ partId, type, refId, inputMode, payloadKey, label, viaCupRelationIds }: {
      partId: string; type: BInterviewerPartItemType; refId: string
      inputMode?: BInterviewerPartItemInputMode; payloadKey?: string; label?: string; viaCupRelationIds?: string[]
    }) =>
      addInterviewerPartItem(partId, {
        type,
        cupTraitId: type === 'trait' ? refId : undefined,
        cupRelationId: type === 'relation' ? refId : undefined,
        globalRelationId: type === 'global' ? refId : undefined,
        inputMode: inputMode ?? 'select',
        payloadKey: payloadKey || undefined,
        label: label || undefined,
        viaCupRelationIds: viaCupRelationIds?.length ? viaCupRelationIds : undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-interviewer-parts', invId] }),
  })
  const removeItem = useMutation({
    mutationFn: (itemId: string) => removeInterviewerPartItem(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-interviewer-parts', invId] }),
  })

  const [editingTitle, setEditingTitle] = useState<Record<string, string>>({})

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-3">Wizard parts</h3>
      <p className="text-xs text-neutral-400 mb-3">Each part is a named page in the wizard. Add traits or relations as questions.</p>

      <div className="space-y-3 mb-4">
        {(parts as BInterviewerPart[]).map((part, pi) => (
          <div key={part.id} className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-neutral-400 w-5">{pi + 1}</span>
              {editingTitle[part.id] !== undefined ? (
                <input
                  value={editingTitle[part.id]}
                  onChange={e => setEditingTitle(p => ({ ...p, [part.id]: e.target.value }))}
                  onBlur={() => {
                    if (editingTitle[part.id] !== part.title) renamePart.mutate({ partId: part.id, title: editingTitle[part.id] })
                    setEditingTitle(p => { const n = { ...p }; delete n[part.id]; return n })
                  }}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                  className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
                  autoFocus
                />
              ) : (
                <button
                  onClick={() => setEditingTitle(p => ({ ...p, [part.id]: part.title }))}
                  className="flex-1 text-left text-sm font-medium text-neutral-800 hover:text-neutral-600"
                >
                  {part.title || <span className="text-neutral-400 italic">untitled part</span>}
                </button>
              )}
              <button onClick={() => removePart.mutate(part.id)} className="text-neutral-300 hover:text-red-500 transition-colors"><X size={13} /></button>
            </div>

            <div className="space-y-1 mb-2 pl-5">
              {(part.items ?? []).map((item: BInterviewerPartItem) => {
                const chained = !!item.viaCupRelationIds?.length
                const trait = !chained ? cupTraits.find((t: BCupTrait) => t.id === item.cupTraitId) : undefined
                const rel   = !chained ? cupRelations.find((r: BCupRelation) => r.id === item.cupRelationId) : undefined
                return (
                  <div key={item.id} className="flex items-center gap-2 text-xs text-neutral-600">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${item.type === 'trait' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
                      {item.type}
                    </span>
                    {chained && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">
                        via {item.viaCupRelationIds!.length} hop{item.viaCupRelationIds!.length > 1 ? 's' : ''}
                      </span>
                    )}
                    <span className="font-medium">
                      {item.label ? item.label : chained
                        ? (item.type === 'trait' ? (item.cupTraitId ?? '').slice(0, 8) : (item.cupRelationId ?? '').slice(0, 8))
                        : item.type === 'trait'
                          ? (trait?.trait?.title ?? (item.cupTraitId ?? '').slice(0, 8))
                          : [
                              allCups.find(c => c.id === rel?.parentCupId)?.title,
                              rel?.label,
                              rel?.relationType,
                            ].filter(Boolean).join(' · ') || (item.cupRelationId ?? '').slice(0, 8)
                      }
                    </span>
                    {item.type === 'relation' && item.inputMode === 'live_search' && (
                      <span className="px-1 py-0.5 rounded text-[10px] bg-teal-50 text-teal-600">live search</span>
                    )}
                    {item.type === 'relation' && item.inputMode === 'from_payload' && (
                      <span className="px-1 py-0.5 rounded text-[10px] bg-amber-50 text-amber-600">payload: {item.payloadKey}</span>
                    )}
                    {item.type === 'relation' && item.altPreparationId && (
                      <span className="px-1 py-0.5 rounded text-[10px] bg-brand-50 text-brand-600">+ {item.altLabel || 'alt action'}</span>
                    )}
                    {item.type === 'relation' && (
                      <AltLinkButton item={item} onSaved={() => qc.invalidateQueries({ queryKey: ['b-interviewer-parts', invId] })} />
                    )}
                    <button onClick={() => removeItem.mutate(item.id)} className={`${item.type === 'relation' ? '' : 'ml-auto '}text-neutral-300 hover:text-red-500`}><X size={11} /></button>
                  </div>
                )
              })}
            </div>

            <AddItemRow
              partId={part.id}
              cupId={cupId}
              allCups={allCups}
              onAdd={p => addItem.mutate(p)}
              adding={addItem.isPending}
            />
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          value={newPartTitle}
          onChange={e => setNewPartTitle(e.target.value)}
          placeholder="New part title"
          className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
          onKeyDown={e => { if (e.key === 'Enter' && newPartTitle) addPart.mutate() }}
        />
        <button
          onClick={() => addPart.mutate()}
          disabled={!newPartTitle || addPart.isPending}
          className="px-2 py-1.5 text-xs rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"
        >
          <Plus size={12} /> Part
        </button>
      </div>
    </section>
  )
}

// Renders the type/target picker for one part's "add item" row, plus an optional growing chain
// of relation hops so the final target can be a trait/relation on a cup reached through 1+
// relations from the interviewer's own cup (see InterviewerPartItem.viaCupRelationIds). Extracted
// into its own component (rather than inlined per-part in PartsEditor) so each part's chain state
// and its own dynamically-scoped trait/relation queries stay isolated per part.
function AddItemRow({ partId, cupId, allCups, onAdd, adding }: {
  partId: string
  cupId: string
  allCups: BCup[]
  onAdd: (payload: {
    partId: string; type: BInterviewerPartItemType; refId: string
    inputMode: BInterviewerPartItemInputMode; payloadKey: string; label: string; viaCupRelationIds: string[]
  }) => void
  adding: boolean
}) {
  const [type, setType] = useState<BInterviewerPartItemType>('trait')
  const [viaChain, setViaChain] = useState<{ relId: string; parentCupId: string; displayLabel: string }[]>([])
  const [refId, setRefId] = useState('')
  const [inputMode, setInputMode] = useState<BInterviewerPartItemInputMode>('select')
  const [payloadKey, setPayloadKey] = useState('')
  const [label, setLabel] = useState('')
  const [hopPick, setHopPick] = useState('')

  // The cup whose own traits/relations are the current "final target" pool — the interviewer's
  // own cup with no hops chosen, or the last chosen hop's parent cup once one or more are added.
  const baseCupId = viaChain.length ? viaChain[viaChain.length - 1].parentCupId : cupId

  const { data: baseCupRelations = [] } = useQuery<BCupRelation[]>({
    queryKey: ['b-cup-child-relations', baseCupId],
    queryFn: () => getCupChildRelations(baseCupId).then(r => r.data),
    enabled: !!baseCupId,
  })
  const { data: baseCupTraits = [] } = useQuery<BCupTrait[]>({
    queryKey: ['b-cup-traits', baseCupId],
    queryFn: () => getCupTraits(baseCupId).then(r => r.data),
    enabled: !!baseCupId && type === 'trait',
  })
  // Global relations (mailbox, users, datasets) of the interviewer's own cup — not reachable
  // through a chain.
  const { data: globalRelations = [] } = useQuery<BCupGlobalRelation[]>({
    queryKey: ['b-cup-global-relations', cupId],
    queryFn: () => getCupGlobalRelations(cupId).then(r => r.data),
    enabled: !!cupId && type === 'global',
  })

  const relLabel = (r: BCupRelation) => {
    const parentCup = allCups.find(c => c.id === r.parentCupId)
    return [parentCup?.title ?? r.parentCupId.slice(0, 8), r.label, r.relationType].filter(Boolean).join(' · ')
  }

  const addHop = () => {
    const rel = baseCupRelations.find(r => r.id === hopPick)
    if (!rel) return
    setViaChain(chain => [...chain, { relId: rel.id, parentCupId: rel.parentCupId, displayLabel: relLabel(rel) }])
    setHopPick('')
    setRefId('')
  }
  const popHopsFrom = (i: number) => {
    setViaChain(chain => chain.slice(0, i))
    setRefId('')
  }
  const reset = () => {
    setType('trait'); setViaChain([]); setRefId(''); setInputMode('select'); setPayloadKey(''); setLabel(''); setHopPick('')
  }

  return (
    <div className="space-y-2 pl-5">
      {viaChain.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[10px] text-neutral-400 uppercase tracking-wide">via</span>
          {viaChain.map((hop, i) => (
            <span key={hop.relId} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-indigo-50 text-indigo-600">
              {hop.displayLabel}
              <button type="button" onClick={() => popHopsFrom(i)} className="text-indigo-400 hover:text-red-500"><X size={9} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-[10px] text-neutral-400 uppercase tracking-wide whitespace-nowrap">reach through a relation</span>
        <select
          value={hopPick}
          onChange={e => setHopPick(e.target.value)}
          className="px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
        >
          <option value="">— none —</option>
          {baseCupRelations.map(r => <option key={r.id} value={r.id}>{relLabel(r)}</option>)}
        </select>
        <button
          type="button"
          onClick={addHop}
          disabled={!hopPick}
          className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          + Add hop
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <select
          value={type}
          onChange={e => { setType(e.target.value as BInterviewerPartItemType); setRefId('') }}
          className="px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
        >
          <option value="trait">trait</option>
          <option value="relation">relation</option>
          {viaChain.length === 0 && <option value="global">global relation</option>}
        </select>
        <select
          value={refId}
          onChange={e => setRefId(e.target.value)}
          className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
        >
          <option value="">— select —</option>
          {type === 'trait'
            ? baseCupTraits.map(t => <option key={t.id} value={t.id}>{t.trait?.title ?? t.id.slice(0, 8)}</option>)
            : type === 'global'
              ? globalRelations.map(g => <option key={g.id} value={g.id}>{g.label} · {GLOBAL_CLASS_TITLES[g.targetClass]} · {g.relationType}</option>)
              : baseCupRelations.map(r => <option key={r.id} value={r.id}>{relLabel(r)}</option>)
          }
        </select>
        {type === 'trait' && (
          <select
            value={inputMode === 'textarea' ? 'textarea' : 'select'}
            onChange={e => setInputMode(e.target.value as BInterviewerPartItemInputMode)}
            className="px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
          >
            <option value="select">single line</option>
            <option value="textarea">multi-line text</option>
          </select>
        )}
        {type === 'relation' && (
          <select
            value={inputMode}
            onChange={e => setInputMode(e.target.value as BInterviewerPartItemInputMode)}
            className="px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
          >
            <option value="select">select</option>
            <option value="live_search">live search</option>
            <option value="from_payload">from payload</option>
          </select>
        )}
        {type === 'relation' && inputMode === 'from_payload' && (
          <input
            value={payloadKey}
            onChange={e => setPayloadKey(e.target.value)}
            placeholder="payload key (e.g. coffeeId)"
            className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
          />
        )}
        <input
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Display label (optional)"
          className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400"
        />
        <button
          onClick={() => {
            if (!refId) return
            onAdd({ partId, type, refId, inputMode, payloadKey, label, viaCupRelationIds: viaChain.map(h => h.relId) })
            reset()
          }}
          disabled={!refId || (type === 'relation' && inputMode === 'from_payload' && !payloadKey) || adding}
          className="px-2 py-1 text-xs rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"
        >
          <Plus size={11} />
        </button>
      </div>
    </div>
  )
}

function ConnectorStepConfig({ step, onClose }: { step: BStep; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: config, isLoading: configLoading } = useQuery<BStepFunnelConnector | null>({
    queryKey: ['b-step-funnel-connector', step.id],
    queryFn: () => getStepFunnelConnector(step.id).then((r) => r.data).catch(() => null),
  })
  const { data: connectors = [], isLoading: connectorsLoading } = useQuery<FunnelConnector[]>({
    queryKey: ['funnel-connectors'],
    queryFn: () => listFunnelConnectors().then((r) => r.data),
  })
  const [selectedId, setSelectedId] = useState('')

  const save = useMutation({
    mutationFn: () => setStepFunnelConnector(step.id, { funnelConnectorId: selectedId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-step-funnel-connector', step.id] }),
  })

  if (configLoading || connectorsLoading) {
    return <Modal title={`Config — ${step.title}`} onClose={onClose}><div className="py-8 text-center text-sm text-neutral-400">Loading</div></Modal>
  }

  const currentConnector = connectors.find((c) => c.id === (config?.funnelConnectorId ?? ''))

  return (
    <Modal title={`Connector config — ${step.title}`} onClose={onClose} size="sm">
      <div className="space-y-4">
        <p className="text-xs text-neutral-500">A connector step runs a funnel connector synchronously when the journey reaches it.</p>
        <Field label="Funnel connector *">
          <select
            defaultValue={config?.funnelConnectorId ?? ''}
            onChange={(e) => setSelectedId(e.target.value)}
            className="field"
          >
            <option value="">Select connector</option>
            {connectors.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </Field>
        {currentConnector && <p className="text-xs text-neutral-400">Current: <span className="font-medium">{currentConnector.title}</span></p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button onClick={() => save.mutate()} disabled={!selectedId || save.isPending}>
            {save.isPending ? 'Saving' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function previewTemplate(template: string): string {
  const now = new Date()
  const year = now.getFullYear().toString()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return template.replace(/\{([^}]+)\}/g, (_m, inner: string) => {
    const parts = inner.split(':')
    const kind = parts[0].toUpperCase()
    if (kind === 'YEAR') return year.slice(-(parts[1] ? parseInt(parts[1]) : 4))
    if (kind === 'MONTH') return month
    if (kind === 'DAY') return day
    if (kind === 'BEAN') return `<${parts.slice(1).join(':')}>`
    if (kind === 'PAD') {
      const pc = parts[1] ?? '0'
      const w = parseInt(parts[2] ?? '4')
      const bTitle = parts.slice(4).join(':')
      return `<${bTitle}>`.padStart(w, pc)
    }
    if (kind === 'SEQ') {
      const pc = parts[1] ?? '0'
      const w = parseInt(parts[2] ?? '4')
      return '1'.padStart(w, pc)
    }
    return `{${inner}}`
  })
}

function GeneratorConfig({ step, onClose }: { step: BStep; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: generator, isLoading } = useQuery<BStepGenerator | null>({
    queryKey: ['b-step-generator', step.id],
    queryFn: () => getStepGenerator(step.id).then((r) => r.data).catch(() => null),
  })
  const [payloadKey, setPayloadKey] = useState('')

  const create = useMutation({
    mutationFn: () => createStepGenerator(step.id, payloadKey || 'coffeeId'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-step-generator', step.id] }),
  })
  const save = useMutation({
    mutationFn: () => updateStepGenerator(generator!.id, { coffeePayloadKey: payloadKey || generator!.coffeePayloadKey }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-step-generator', step.id] }),
  })

  if (isLoading) return <Modal title={`Config — ${step.title}`} onClose={onClose}><div className="py-8 text-center text-sm text-neutral-400">Loading</div></Modal>

  return (
    <Modal title={`Generator config — ${step.title}`} onClose={onClose} size="lg">
      <div className="space-y-5 max-h-[80vh] overflow-y-auto pr-1">
        <p className="text-xs text-neutral-500">
          A generator step auto-computes bean values from templates containing date parts, other bean values, and sequential counters.
        </p>

        <div>
          <Field label="Coffee payload key" description="Key in the journey payload holding the coffeeId whose beans will be written">
            <input
              defaultValue={generator?.coffeePayloadKey ?? 'coffeeId'}
              onChange={(e) => setPayloadKey(e.target.value)}
              placeholder="coffeeId"
              className="field font-mono"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pb-2 border-b border-slate-100">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button
            onClick={() => (generator ? save : create).mutate()}
            disabled={create.isPending || save.isPending}
          >
            {create.isPending || save.isPending ? 'Saving' : generator ? 'Update' : 'Save'}
          </Button>
        </div>

        {generator && <GeneratorOutputsEditor generator={generator} stepId={step.id} />}
      </div>
    </Modal>
  )
}

function GeneratorOutputsEditor({ generator, stepId }: { generator: BStepGenerator; stepId: string }) {
  const qc = useQueryClient()
  const outputs = (generator.outputs ?? []).slice().sort((a, b) => a.position - b.position)

  const [newForm, setNewForm] = useState({ targetTraitTitle: '', template: '', overwriteIfSet: false, position: outputs.length })
  const [editState, setEditState] = useState<Record<string, { targetTraitTitle: string; template: string; overwriteIfSet: boolean; position: number }>>({})

  const invalidate = () => qc.invalidateQueries({ queryKey: ['b-step-generator', stepId] })

  const addOut = useMutation({
    mutationFn: () => addGeneratorOutput(generator.id, newForm),
    onSuccess: () => {
      invalidate()
      setNewForm({ targetTraitTitle: '', template: '', overwriteIfSet: false, position: outputs.length + 1 })
    },
  })
  const saveOut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof editState[string] }) => updateGeneratorOutput(id, data),
    onSuccess: (_r, { id }) => { invalidate(); setEditState(s => { const n = { ...s }; delete n[id]; return n }) },
  })
  const delOut = useMutation({
    mutationFn: (id: string) => deleteGeneratorOutput(id),
    onSuccess: invalidate,
  })

  const startEdit = (o: BStepGeneratorOutput) =>
    setEditState(s => ({ ...s, [o.id]: { targetTraitTitle: o.targetTraitTitle, template: o.template, overwriteIfSet: o.overwriteIfSet, position: o.position } }))

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Outputs</h3>
      <p className="text-xs text-neutral-400 mb-3">
        Each output writes one bean. Tokens: <code className="bg-slate-100 px-1 rounded">{'{YEAR}'}</code>{' '}
        <code className="bg-slate-100 px-1 rounded">{'{YEAR:2}'}</code>{' '}
        <code className="bg-slate-100 px-1 rounded">{'{MONTH}'}</code>{' '}
        <code className="bg-slate-100 px-1 rounded">{'{DAY}'}</code>{' '}
        <code className="bg-slate-100 px-1 rounded">{'{BEAN:title}'}</code>{' '}
        <code className="bg-slate-100 px-1 rounded">{'{PAD:0:4:BEAN:title}'}</code>{' '}
        <code className="bg-slate-100 px-1 rounded">{'{SEQ:0:6}'}</code>{' '}
        <code className="bg-slate-100 px-1 rounded">{'{SEQ:0:4:year}'}</code>
      </p>

      <div className="space-y-2 mb-4">
        {outputs.map((o) => {
          const ed = editState[o.id]
          return (
            <div key={o.id} className="border border-slate-200 rounded-lg p-3 space-y-2">
              {ed ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-neutral-400 uppercase tracking-wide">Bean title</label>
                      <input
                        value={ed.targetTraitTitle}
                        onChange={e => setEditState(s => ({ ...s, [o.id]: { ...s[o.id], targetTraitTitle: e.target.value } }))}
                        className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400 font-mono mt-0.5"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-neutral-400 uppercase tracking-wide">Position</label>
                      <input
                        type="number"
                        value={ed.position}
                        onChange={e => setEditState(s => ({ ...s, [o.id]: { ...s[o.id], position: parseInt(e.target.value) || 0 } }))}
                        className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400 mt-0.5"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-neutral-400 uppercase tracking-wide">Template</label>
                    <input
                      value={ed.template}
                      onChange={e => setEditState(s => ({ ...s, [o.id]: { ...s[o.id], template: e.target.value } }))}
                      className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400 font-mono mt-0.5"
                    />
                  </div>
                  {ed.template && (
                    <p className="text-[10px] text-neutral-400">Preview: <span className="font-mono text-neutral-700">{previewTemplate(ed.template)}</span></p>
                  )}
                  <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ed.overwriteIfSet}
                      onChange={e => setEditState(s => ({ ...s, [o.id]: { ...s[o.id], overwriteIfSet: e.target.checked } }))}
                      className="rounded"
                    />
                    Overwrite if bean already set
                  </label>
                  <div className="flex gap-2 justify-end">
                    <Button variant="secondary" size="sm" onClick={() => setEditState(s => { const n = { ...s }; delete n[o.id]; return n })}>Cancel</Button>
                    <Button
                      size="sm"
                      onClick={() => saveOut.mutate({ id: o.id, data: ed })}
                      disabled={!ed.targetTraitTitle || !ed.template || saveOut.isPending}
                    >
                      {saveOut.isPending ? 'Saving' : 'Save'}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-[10px] text-neutral-400 w-4 mt-0.5">{o.position}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-neutral-800 font-mono">{o.targetTraitTitle}</span>
                      {o.overwriteIfSet && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-600">overwrite</span>}
                    </div>
                    <p className="text-theme-xs text-neutral-500 font-mono mt-0.5 truncate">{o.template}</p>
                    <p className="text-[10px] text-neutral-400 mt-0.5">→ <span className="font-mono text-neutral-600">{previewTemplate(o.template)}</span></p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => startEdit(o)} className="text-neutral-400 hover:text-neutral-700 transition-colors"><Pencil size={12} /></button>
                    <button onClick={() => delOut.mutate(o.id)} className="text-neutral-300 hover:text-red-500 transition-colors"><Trash2 size={12} /></button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Add output form */}
      <div className="border border-dashed border-slate-300 rounded-lg p-3 space-y-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Add output</h4>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-neutral-400 uppercase tracking-wide">Bean title *</label>
            <input
              value={newForm.targetTraitTitle}
              onChange={e => setNewForm(f => ({ ...f, targetTraitTitle: e.target.value }))}
              placeholder="reference"
              className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400 font-mono mt-0.5"
            />
          </div>
          <div>
            <label className="text-[10px] text-neutral-400 uppercase tracking-wide">Position</label>
            <input
              type="number"
              value={newForm.position}
              onChange={e => setNewForm(f => ({ ...f, position: parseInt(e.target.value) || 0 }))}
              className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400 mt-0.5"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] text-neutral-400 uppercase tracking-wide">Template *</label>
          <input
            value={newForm.template}
            onChange={e => setNewForm(f => ({ ...f, template: e.target.value }))}
            placeholder="{YEAR}{SEQ:0:6:year}"
            className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400 font-mono mt-0.5"
          />
        </div>
        {newForm.template && (
          <p className="text-[10px] text-neutral-400">Preview: <span className="font-mono text-neutral-700">{previewTemplate(newForm.template)}</span></p>
        )}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer">
            <input
              type="checkbox"
              checked={newForm.overwriteIfSet}
              onChange={e => setNewForm(f => ({ ...f, overwriteIfSet: e.target.checked }))}
              className="rounded"
            />
            Overwrite if bean already set
          </label>
          <button
            onClick={() => addOut.mutate()}
            disabled={!newForm.targetTraitTitle || !newForm.template || addOut.isPending}
            className="px-2 py-1.5 text-xs rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60 flex items-center gap-1"
          >
            <Plus size={11} /> Add
          </button>
        </div>
      </div>
    </section>
  )
}

function DocumentGeneratorConfig({ step, onClose }: { step: BStep; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: docGen, isLoading } = useQuery<BStepDocumentGenerator | null>({
    queryKey: ['b-step-document-generator', step.id],
    queryFn: () => getStepDocumentGenerator(step.id).then((r) => r.data).catch(() => null),
  })
  const { data: datasets = [] } = useQuery<Dataset[]>({
    queryKey: ['storage-datasets'],
    queryFn: () => getDatasets().then(r => r.data),
  })

  const [templateId, setTemplateId] = useState('')
  const [datasetId, setDatasetId] = useState('')
  const [coffeePayloadKey, setCoffeePayloadKey] = useState('')
  const [cupRelationId, setCupRelationId] = useState('')
  const [script, setScript] = useState('')

  useEffect(() => {
    if (docGen) {
      setTemplateId(docGen.templateId)
      setDatasetId(docGen.datasetId)
      setCoffeePayloadKey(docGen.coffeePayloadKey)
      setCupRelationId(docGen.cupRelationId)
      setScript(docGen.script)
    }
  }, [docGen])

  const create = useMutation({
    mutationFn: () => createStepDocumentGenerator({
      stepId: step.id, templateId, datasetId, coffeePayloadKey: coffeePayloadKey || 'coffeeId', script, cupRelationId,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-step-document-generator', step.id] }),
  })
  const save = useMutation({
    mutationFn: () => updateStepDocumentGenerator(docGen!.id, { templateId, datasetId, coffeePayloadKey: coffeePayloadKey || 'coffeeId', script, cupRelationId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-step-document-generator', step.id] }),
  })
  const canSave = !!templateId && !!datasetId && !!cupRelationId

  if (isLoading) return <Modal title={`Config — ${step.title}`} onClose={onClose}><div className="py-8 text-center text-sm text-neutral-400">Loading</div></Modal>

  return (
    <Modal title={`Document generator config — ${step.title}`} onClose={onClose} size="lg">
      <div className="space-y-5 max-h-[80vh] overflow-y-auto pr-1">
        <p className="text-xs text-neutral-500">
          Fills a template (today: a storage-service PDF template) from a script's computed payload, stores the
          result in a dataset, and attaches it to the source coffee as a new coffee on the given cup relation.
        </p>

        <Field label="Template ID *" description="A storage-service PDF template id, e.g. from /pdf-templates/&lt;id&gt;. No picker yet — paste the id.">
          <input value={templateId} onChange={(e) => setTemplateId(e.target.value)} placeholder="e.g. e99a0b0d-cede-4578-a233-a86554792be0" className="field font-mono" />
        </Field>

        <Field label="Dataset *" description="Where the generated document is stored in storage-service">
          <select value={datasetId} onChange={(e) => setDatasetId(e.target.value)} className="field">
            <option value="">Select dataset</option>
            {(datasets as Dataset[]).map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </Field>

        <Field label="Coffee payload key" description="Key in the journey payload holding the source coffeeId the document is generated for and attached to">
          <input value={coffeePayloadKey} onChange={(e) => setCoffeePayloadKey(e.target.value)} placeholder="coffeeId" className="field font-mono" />
        </Field>

        <Field label="Cup relation ID *" description="The CupRelation (source cup → document cup) the new document is linked through. No picker yet — paste the id from the Cups page.">
          <input value={cupRelationId} onChange={(e) => setCupRelationId(e.target.value)} placeholder="cup relation id" className="field font-mono" />
        </Field>

        <Field label="Script">
          <div className="text-xs text-ink-dim mb-2 space-y-0.5">
            <p>Async JS, same sandbox as a Drinker. Must <code className="bg-paper-sunken px-1 rounded">return</code> a flat object whose keys match the template's variable names — this becomes the document's payload, not the journey payload.</p>
            <p className="font-mono text-theme-xs leading-5 bg-paper-sunken border border-paper-border rounded p-2 mt-1 whitespace-pre">
{`ctx.getCoffee(id)                              → { id, beans } | null
ctx.getParent(coffeeId, parentCupTitle, label) → { id, beans } | null
ctx.getChildren(coffeeId, childCupTitle, label) → [{ id, beans }]
ctx.log(message)                               → captured in step logs

Optional "documentTitle" key names the attached document (falls back to a default).`}
            </p>
          </div>
          <textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            rows={12}
            placeholder={`// Example: purchase-order payload for a Preorder
const preorder = await ctx.getCoffee(preorderId);
return {
  documentTitle: 'Purchase Order ' + preorder.beans['Garage Preorder.reference'],
  poNumber: preorder.beans['Garage Preorder.reference'],
};`}
            className="field font-mono resize-none"
          />
        </Field>

        <div className="flex justify-end gap-2 pb-2 border-b border-slate-100">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button
            onClick={() => (docGen ? save : create).mutate()}
            disabled={!canSave || create.isPending || save.isPending}
          >
            {create.isPending || save.isPending ? 'Saving' : docGen ? 'Update' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function KVSection({ title, items, form, setForm, fields, onAdd, addPending, onRemove, render }: {
  title: string; items: any[]; form: Record<string, string>; setForm: (f: any) => void
  fields: { k: string; ph: string }[]; onAdd: () => void; addPending: boolean
  onRemove: (id: string) => void; render: (item: any) => React.ReactNode
}) {
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">{title}</h3>
      <div className="space-y-1 mb-2">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-100 text-xs">
            <div>{render(item)}</div>
            <button onClick={() => onRemove(item.id)} className="text-neutral-300 hover:text-red-500 transition-colors"><X size={12} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        {fields.map((f) => (
          <input key={f.k} value={form[f.k] ?? ''} onChange={(e) => setForm((s: any) => ({ ...s, [f.k]: e.target.value }))} placeholder={f.ph}
            className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400" />
        ))}
        <button onClick={onAdd} disabled={addPending}
          className="px-2 py-1.5 text-xs rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"><Plus size={12} /></button>
      </div>
    </section>
  )
}
